import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useMemo, useState } from 'react';
// Types
import { StreamStatus } from 'api/graphql-global-types';
// Api
import { GET_STREAM } from 'api/streams/queries';
import { GetStream, GetStreamVariables } from 'api/streams/types/GetStream';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import {
  useGetStreamBySlug,
  useGetFeaturedStreams,
  useRefetchOnUserLogIn,
} from 'hooks';
// Helpers
import {
  generateSportsEventStructuredData,
  cleanDescription,
} from 'helpers/structured-data';
import {
  ApolloPageContext,
  PageProps,
  getCleanQueryParam,
  markPageAsNotFound,
} from 'helpers/next-page';
import {
  computeOrderStreamPath,
  computeVideoWatchStreamPath,
  getEnvLink,
} from 'helpers/routes';
// Layouts
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
import StripeElements from 'layouts/StripeElements/StripeElements';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';
import OrderStream from 'components/OrderStream/OrderStream';
import PurchaseSuccessModal from 'components/PurchaseSuccessModal/PurchaseSuccessModal';
import { usePersonMentionPageVisitTracking } from 'hooks/usePersonMentionPageVisitTracking';

const OrderStreamPageContent = (): JSX.Element => {
  usePersonMentionPageVisitTracking();
  const { data, refetch, updateQuery } = useGetStreamBySlug();
  const { data: featuredStreamsData } = useGetFeaturedStreams();
  const stream = data?.stream;

  const isFeaturedStream = useMemo(() => {
    if (!stream?.id || !featuredStreamsData?.featuredPpvEvents) {
      return false;
    }
    return featuredStreamsData.featuredPpvEvents.some(
      (event) => event.stream.id === stream.id
    );
  }, [stream?.id, featuredStreamsData?.featuredPpvEvents]);

  // Show purchase success modal if redirected here after checkout
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const flag = localStorage.getItem('showPurchaseSuccess');
      if (flag === 'true') {
        setShowPurchaseSuccess(true);
        localStorage.removeItem('showPurchaseSuccess');
      }
    }
  }, []);

  const handleClosePurchaseModal = () => {
    setShowPurchaseSuccess(false);
  };

  useRefetchOnUserLogIn({ refetch });
  const streamName = stream?.name;
  const isPastStream = stream?.streamStatus === StreamStatus.Ended;
  const metaTitle = stream ? `${streamName} | MILLIONS` : 'Loading...';

  const metaDescription = (() => {
    if (!stream) return '';

    const MAX = 160;

    // Personal name is more searchable than a store brand name
    const creatorPersonalName =
      stream.store?.firstName && stream.store?.lastName
        ? `${stream.store.firstName} ${stream.store.lastName}`
        : null;
    const creatorName =
      creatorPersonalName || stream.store?.storeDetails?.storeName || null;

    // Clean: strip HTML, URLs, MILLIONS boilerplate, and "Starts at X PM TZ"
    const cleanDesc = cleanDescription(stream.description)
      .replace(/https?:\/\/\S+/g, '')
      .replace(/\bExclusively on MILLIONS\.?\s*/gi, '')
      .replace(/\bStarts at [^.]+\.?\s*/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    const accessNote =
      stream.isFree === false &&
      typeof stream.price === 'number' &&
      stream.price > 0
        ? `$${stream.price} PPV`
        : stream.isFree
        ? 'Free to watch'
        : '';

    if (isPastStream) {
      // Strip "| LIVE!" / "- LIVE!" — stream is no longer live
      const pastName = (streamName || '')
        .replace(/[\s|–-]+LIVE!?\s*$/i, '')
        .trim();
      const base = creatorName
        ? `Watch the replay of ${pastName} by ${creatorName} on MILLIONS.`
        : `Watch the replay of ${pastName} on MILLIONS.`;
      const sentences = cleanDesc.match(/[^.!?]+[.!?]+\s*/g) || [];
      let suffix = '';
      for (const s of sentences) {
        if (`${base} ${suffix}${s}`.length <= MAX) suffix += s;
        else break;
      }
      return suffix ? `${base} ${suffix.trim()}` : base;
    }

    // Upcoming / Active — lead with stream content, then attribute creator
    const sentences = cleanDesc.match(/[^.!?]+[.!?]+\s*/g) || [];
    const attribution = creatorName
      ? `Hosted by ${creatorName} on MILLIONS.${
          accessNote ? ` ${accessNote}.` : ''
        }`
      : `On MILLIONS.${accessNote ? ` ${accessNote}.` : ''}`;

    let body = '';
    for (const s of sentences) {
      const next = body ? `${body}${s}` : s.trim();
      if (`${next} ${attribution}`.length <= MAX) body = next;
      else if (!body) {
        body = s.trim();
        break;
      } else break;
    }

    if (body) {
      const full = `${body.trim()} ${attribution}`;
      return full.length <= MAX ? full : body.trim();
    }

    return creatorName
      ? `Live stream by ${creatorName} on MILLIONS.${
          accessNote ? ` ${accessNote}.` : ''
        }`
      : `Live stream on MILLIONS.`;
  })();

  const canonicalUrl =
    stream?.store?.slug && stream?.slug
      ? `${getEnvLink()}${
          isPastStream
            ? computeVideoWatchStreamPath(stream)
            : computeOrderStreamPath(stream)
        }`
      : null;

  return (
    <>
      <Head
        title={metaTitle}
        description={metaDescription}
        image={stream?.imageURL || ''}
        structuredData={generateSportsEventStructuredData(stream)}
        canonicalLink={canonicalUrl}
      />
      <AppBannerAndModal
        when={!!stream}
        variant="profile"
        username={stream?.store?.slug ?? undefined}
        profileImageUrl={
          stream?.store?.storeDetails?.avatarURL ??
          stream?.imageURL ??
          undefined
        }
      />
      <Header />
      <Main withoutSidesPadding>
        <StripeElements>
          <OrderStream
            stream={stream}
            updateStreamQuery={updateQuery}
            isFeaturedStream={isFeaturedStream}
          />
        </StripeElements>
      </Main>
      <Footer />

      <PurchaseSuccessModal
        open={showPurchaseSuccess}
        onClose={handleClosePurchaseModal}
      />
    </>
  );
};

const OrderStreamPage: NextPage<PageProps> = ({ isNotFound }) => {
  if (isNotFound) {
    return <NotFoundPage />;
  }

  return <OrderStreamPageContent />;
};

OrderStreamPage.getInitialProps = async (
  ctx: ApolloPageContext
): Promise<PageProps> => {
  const athleteSlug = getCleanQueryParam(ctx.query.athleteSlug);
  const streamSlug = getCleanQueryParam(ctx.query.streamSlug);

  if (!ctx.apolloClient || !athleteSlug || !streamSlug) {
    return {};
  }

  try {
    const { data } = await ctx.apolloClient.query<
      GetStream,
      GetStreamVariables
    >({
      query: GET_STREAM,
      variables: {
        input: {
          slug: streamSlug,
          storeSlug: athleteSlug,
        },
      },
    });

    if (!data?.stream) {
      markPageAsNotFound(ctx);
      return { isNotFound: true };
    }
  } catch (error) {
    // Client-side navigations can fail transiently (aborted fetch, Apollo
    // store reset while logging out mid-watch). The page's own query retries
    // those — turning them into a permanent 404 would strand the user.
    if (!ctx.res) {
      return {};
    }

    Sentry.captureException(error, {
      tags: { section: 'ssr', page: 'streams/[streamSlug]' },
    });
    markPageAsNotFound(ctx);
    return { isNotFound: true };
  }

  return {};
};

export { OrderStreamPage };

export default withApollo(OrderStreamPage, { getDataFromTree });
